import React, { useMemo, useState } from 'react';
import { Paper, Button, Box, Typography, CircularProgress, Alert } from '@mui/material';
import { VisibilityRounded } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { FilterBar, Field, SearchInput, SelectInput, Spacer, ResetButton } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import NameCell from '../../components/NameCell';
import StatusChip from '../../components/StatusChip';
import { useToast } from '../../components/Toast';
import { useConsumers, useTokens, ConsumerListItem, TokenListItem } from '../../hooks/useConsumers';
import { money, statusLabel } from '../../utils/format';

const ConsumersPage: React.FC = () => {
  const toast = useToast();
  const consumers = useConsumers();
  const tokens = useTokens();

  const [cq, setCq] = useState('');
  const [city, setCity] = useState('');
  const [cStatus, setCStatus] = useState('');
  const [tq, setTq] = useState('');
  const [tStatus, setTStatus] = useState('');

  const consumerRows = useMemo(
    () =>
      consumers.items.filter(
        (r) =>
          (!cq || (r.name + r.phone + r.city + r.id).toLowerCase().includes(cq.toLowerCase())) &&
          (!city || r.city === city) && (!cStatus || r.status === cStatus)
      ),
    [consumers.items, cq, city, cStatus]
  );
  const tokenRows = useMemo(
    () =>
      tokens.items.filter(
        (r) =>
          (!tq || (r.consumer + r.queue + r.business + r.number).toLowerCase().includes(tq.toLowerCase())) &&
          (!tStatus || r.status === tStatus)
      ),
    [tokens.items, tq, tStatus]
  );

  const cCols: Column<ConsumerListItem>[] = [
    { key: 'name', label: 'Consumer', csv: (r) => r.name, render: (r) => <NameCell name={r.name} /> },
    { key: 'phone', label: 'Phone' },
    { key: 'city', label: 'City' },
    { key: 'tokens', label: 'Tokens', align: 'right' },
    { key: 'lastVisit', label: 'Last Visit' },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} /> },
    {
      key: '_', label: '', render: (r) => (
        <Button component={Link} to={`/consumers/${r.id}`} size="small" variant="outlined" startIcon={<VisibilityRounded />}
          sx={{ borderColor: 'divider', color: 'text.primary' }}>View</Button>
      ),
    },
  ];

  const tCols: Column<TokenListItem>[] = [
    { key: 'number', label: 'Token', render: (r) => <Typography component="span" sx={{ fontWeight: 600 }}>{r.number}</Typography> },
    { key: 'consumer', label: 'Consumer' },
    { key: 'queue', label: 'Queue' },
    { key: 'business', label: 'Business' },
    { key: 'date', label: 'Date' },
    { key: 'amount', label: 'Amount', align: 'right', csv: (r) => r.amount, render: (r) => (r.amount ? money(r.amount) : '—') },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
    {
      key: '_', label: '', render: (r) => (
        <Button onClick={() => toast(`Token ${r.number} — ${r.consumer}`, 'info')} size="small" variant="outlined" startIcon={<VisibilityRounded />}
          sx={{ borderColor: 'divider', color: 'text.primary' }}>View</Button>
      ),
    },
  ];

  if (consumers.loading || tokens.loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  const err = consumers.error || tokens.error;
  if (err) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => { consumers.reload(); tokens.reload(); }}>Retry</Button>}>{err}</Alert>;
  }

  return (
    <>
      <FilterBar>
        <Field label="Search consumer"><SearchInput value={cq} onChange={setCq} placeholder="Name, phone, city…" /></Field>
        <Field label="City"><SelectInput value={city} onChange={setCity} options={[{ value: '', label: 'All cities' }, ...consumers.filters.cities]} /></Field>
        <Field label="Status"><SelectInput value={cStatus} onChange={setCStatus} options={[{ value: '', label: 'All statuses' }, ...consumers.filters.statuses.map((s) => ({ value: s, label: statusLabel(s) }))]} /></Field>
        <Spacer />
        <ResetButton onClick={() => { setCq(''); setCity(''); setCStatus(''); }} />
      </FilterBar>

      <Paper sx={{ p: 0, mb: 3 }}>
        <DataTable columns={cCols} rows={consumerRows} title="Consumers" />
      </Paper>

      <FilterBar>
        <Field label="Search token"><SearchInput value={tq} onChange={setTq} placeholder="Consumer, queue, business…" /></Field>
        <Field label="Status">
          <SelectInput value={tStatus} onChange={setTStatus} options={[{ value: '', label: 'All statuses' }, ...tokens.filters.statuses.map((s) => ({ value: s, label: statusLabel(s) }))]} minWidth={160} />
        </Field>
        <Spacer />
      </FilterBar>

      <Paper sx={{ p: 0 }}>
        <DataTable columns={tCols} rows={tokenRows} title="Token Details" />
      </Paper>
      <Box sx={{ height: 8 }} />
    </>
  );
};

export default ConsumersPage;
